import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const DEFAULTS = {
  theme: "light", // light | dark | mono
  accent: "blue", // blue | emerald | amber | rose | violet
  font: "inter", // inter | grotesk | mono | serif
  density: "comfortable", // compact | comfortable
  sidebar: "left", // left | right
  sidebarBg: "", // hex string or empty (use theme default)
  contentBg: "", // hex string or empty (use theme default)
};

const SettingsContext = createContext(null);

const ACCENT_VARS = {
  blue: { primary: "29 78 216", primaryFg: "255 255 255" },
  emerald: { primary: "5 122 85", primaryFg: "255 255 255" },
  amber: { primary: "180 83 9", primaryFg: "255 255 255" },
  rose: { primary: "190 18 60", primaryFg: "255 255 255" },
  violet: { primary: "109 40 217", primaryFg: "255 255 255" },
};

const THEME_VARS = {
  light: {
    bg: "250 250 250",
    surface: "255 255 255",
    fg: "9 9 11",
    muted: "113 113 122",
    border: "228 228 231",
  },
  dark: {
    bg: "9 9 11",
    surface: "24 24 27",
    fg: "250 250 250",
    muted: "161 161 170",
    border: "39 39 42",
  },
  mono: {
    bg: "255 255 255",
    surface: "255 255 255",
    fg: "0 0 0",
    muted: "82 82 91",
    border: "0 0 0",
  },
};

const FONT_FAMILIES = {
  inter: "'Inter', system-ui, sans-serif",
  grotesk: "'Space Grotesk', 'Cabinet Grotesk', system-ui, sans-serif",
  mono: "'JetBrains Mono', 'IBM Plex Mono', monospace",
  serif: "'IBM Plex Serif', 'Source Serif Pro', Georgia, serif",
};

// "#1e3a8a" -> "30 58 138"
function hexToRgbTriplet(hex) {
  if (!hex) return null;
  const v = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(v)) return null;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

// Compute YIQ luminance to decide light vs dark
function isDarkHex(hex) {
  if (!hex) return null;
  const v = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(v)) return null;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 140;
}

function applySettings(s) {
  const root = document.documentElement;
  const t = THEME_VARS[s.theme] || THEME_VARS.light;
  const a = ACCENT_VARS[s.accent] || ACCENT_VARS.blue;
  root.style.setProperty("--vsm-bg", t.bg);
  root.style.setProperty("--vsm-surface", t.surface);
  root.style.setProperty("--vsm-fg", t.fg);
  root.style.setProperty("--vsm-muted", t.muted);
  root.style.setProperty("--vsm-border", t.border);
  root.style.setProperty("--vsm-primary", a.primary);
  root.style.setProperty("--vsm-primary-fg", a.primaryFg);
  root.style.setProperty("--vsm-font", FONT_FAMILIES[s.font] || FONT_FAMILIES.inter);

  const sidebarRgb = hexToRgbTriplet(s.sidebarBg);
  const contentRgb = hexToRgbTriplet(s.contentBg);
  root.style.setProperty("--vsm-sidebar-bg", sidebarRgb || t.surface);
  root.style.setProperty("--vsm-content-bg", contentRgb || t.bg);

  const sidebarDark = isDarkHex(s.sidebarBg);
  const contentDark = isDarkHex(s.contentBg);
  // When a custom dark bg is chosen, flip text to light; when light custom bg, force dark text
  if (sidebarDark === true) {
    root.dataset.vsmSidebarDark = "true";
  } else {
    delete root.dataset.vsmSidebarDark;
  }
  if (contentDark === true) {
    root.dataset.vsmContentDark = "true";
  } else {
    delete root.dataset.vsmContentDark;
  }

  root.dataset.vsmTheme = s.theme;
  root.dataset.vsmDensity = s.density;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem("vsm_settings");
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch (e) {
      console.warn("Settings persistence failed:", e);
    }
    return DEFAULTS;
  });

  useEffect(() => {
    applySettings(settings);
    localStorage.setItem("vsm_settings", JSON.stringify(settings));
  }, [settings]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const reset = () => setSettings(DEFAULTS);

  return (
    <SettingsContext.Provider value={useMemo(() => ({ settings, update, reset }), [settings])}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
